import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import EnrollUserIntoTrainingPlanDialog from './EnrollUserIntoTrainingPlanDialog';
import { apiUrl } from '../../config';

const cardSx = {
  border: '1px solid',
  borderColor: '#dbe3ee',
  borderRadius: 1,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
};

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'workouts', label: 'Workouts' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'check-ins', label: 'Check-ins' },
  { value: 'messages', label: 'Messages' },
  { value: 'photos', label: 'Photos' },
  { value: 'notes', label: 'Notes' },
  { value: 'plans', label: 'Plans' },
  { value: 'payments', label: 'Payments' },
];

const formatDate = (date, fallback = '-') => {
  if (!date) {
    return fallback;
  }

  return moment(date).isValid() ? moment(date).format('MMM D, YYYY') : fallback;
};

const formatTime = (date) => {
  if (!date || !moment(date).isValid()) {
    return '';
  }

  return moment(date).format('h:mm A');
};

const dateGroupLabel = (date) => {
  if (!date || !moment(date).isValid()) {
    return 'Unknown date';
  }

  const value = moment(date);
  if (value.isSame(moment(), 'day')) {
    return `Today - ${value.format('MMM D, YYYY')}`;
  }
  if (value.isSame(moment().subtract(1, 'day'), 'day')) {
    return `Yesterday - ${value.format('MMM D, YYYY')}`;
  }
  if (value.isAfter(moment().subtract(7, 'day'))) {
    return 'Last Week';
  }

  return value.format('MMM D, YYYY');
};

const getActivePlan = (sessions = []) =>
  sessions.find((session) => session.status === 'ACTIVE') || sessions[0];

const getActivityType = (activity = {}) => {
  const title = (activity.title || '').toLowerCase();

  if (activity.workoutSession) {
    return 'workouts';
  }
  if (activity.mealLog || activity.meal) {
    return 'nutrition';
  }
  if (title.includes('check')) {
    return 'check-ins';
  }
  if (title.includes('message')) {
    return 'messages';
  }
  if (title.includes('photo')) {
    return 'photos';
  }
  if (title.includes('note')) {
    return 'notes';
  }
  if (title.includes('plan') || title.includes('program')) {
    return 'plans';
  }
  if (title.includes('payment') || title.includes('subscription')) {
    return 'payments';
  }

  return 'all';
};

const getActivityIcon = (activity = {}) => {
  const type = getActivityType(activity);

  if (type === 'workouts') {
    return <FitnessCenterOutlinedIcon />;
  }
  if (type === 'nutrition') {
    return <RestaurantOutlinedIcon />;
  }
  if (type === 'photos') {
    return <AddPhotoAlternateOutlinedIcon />;
  }
  if (type === 'plans') {
    return <AssignmentOutlinedIcon />;
  }
  if (type === 'notes') {
    return <NotesOutlinedIcon />;
  }

  return <SendOutlinedIcon />;
};

const getActivityColor = (activity = {}) => {
  const type = getActivityType(activity);

  if (type === 'workouts') {
    return { bg: '#dcfce7', color: '#15803d' };
  }
  if (type === 'nutrition') {
    return { bg: '#ede9fe', color: '#4f46e5' };
  }
  if (type === 'photos') {
    return { bg: '#ffedd5', color: '#f97316' };
  }
  if (type === 'plans') {
    return { bg: '#dcfce7', color: '#16a34a' };
  }
  if (type === 'notes') {
    return { bg: '#eef2ff', color: '#4f46e5' };
  }

  return { bg: '#f3f4f6', color: '#475569' };
};

const getActivityTitle = (activity = {}) => {
  if (activity.title) {
    return activity.title;
  }
  if (activity.workoutSession) {
    return 'Completed Workout';
  }
  if (activity.mealLog) {
    return 'Logged Meal';
  }
  return 'Activity';
};

const getActivityDetail = (activity = {}) => {
  if (activity.workout?.title) {
    return activity.workout.title;
  }
  if (activity.meal?.title) {
    return activity.meal.title;
  }
  if (activity.workoutSession?.notes) {
    return activity.workoutSession.notes;
  }
  if (activity.workoutSession?.feel) {
    return activity.workoutSession.feel;
  }
  if (activity.mealLog?.notes) {
    return activity.mealLog.notes;
  }
  return activity.desc || '';
};

const groupActivities = (activities = []) =>
  activities.reduce((groups, activity) => {
    const label = dateGroupLabel(activity.createdAt);
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(activity);
    return groups;
  }, {});

function AboutCard({ user }) {
  const trainers = user.trainerIds || [];

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.25 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 900, mb: 2 }}>About</Typography>
        <Stack spacing={2.25}>
          <InfoRow icon={<EmailOutlinedIcon />} label="Email" value={user.email || '-'} />
          <InfoRow icon={<LocationOnOutlinedIcon />} label="Location" value={user.location || '-'} />
          <InfoRow icon={<ScheduleOutlinedIcon />} label="Timezone" value={user.timezone || '-'} />
          <InfoRow icon={<CalendarTodayOutlinedIcon />} label="Member Since" value={formatDate(user.createdAt)} />
          <InfoRow
            icon={<PersonOutlineIcon />}
            label="Trainers"
            value={trainers.length ? trainers.join(', ') : '-'}
            link={trainers[0] ? `/admin/trainers/${trainers[0]}` : null}
          />
          <InfoRow
            icon={<FitnessCenterOutlinedIcon />}
            label="Completed Workouts"
            value={user.workoutSessionCount ?? 0}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function InfoRow({ icon, label, value, link }) {
  const content = link ? (
    <Typography
      component={RouterLink}
      to={link}
      sx={{ color: '#4338ca', fontSize: 14, textDecoration: 'none' }}
    >
      {value}
    </Typography>
  ) : (
    <Typography sx={{ color: '#40536b', fontSize: 14, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  );

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ color: '#48617a', pt: 0.25 }}>{React.cloneElement(icon, { fontSize: 'small' })}</Box>
      <Box minWidth={0}>
        <Typography sx={{ color: '#40536b', fontSize: 13, mb: 0.25 }}>{label}</Typography>
        {content}
      </Box>
    </Stack>
  );
}

function QuickActions({ activePlan, onAddTestData, onAssignPlan, onTabChange }) {
  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.25 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 900, mb: 2 }}>
          Quick Actions
        </Typography>
        <Stack spacing={1}>
          {activePlan?.trainingPlan?._id && (
            <ActionLink
              icon={<AssignmentOutlinedIcon />}
              label="View Training Plan"
              to={`/trainer/training_plans/${activePlan.trainingPlan._id}`}
            />
          )}
          <ActionButton icon={<RestaurantOutlinedIcon />} label="View Nutrition Plan" onClick={() => onTabChange?.('nutrition')} />
          <ActionButton icon={<EditNoteOutlinedIcon />} label="Add Note" onClick={() => onTabChange?.('notes')} />
          <ActionButton icon={<AssignmentOutlinedIcon />} label="Assign Training Plan" onClick={onAssignPlan} />
          <ActionButton icon={<FitnessCenterOutlinedIcon />} label="Add Test Data" onClick={onAddTestData} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function ActionLink({ icon, label, to }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={icon}
      sx={{ color: '#4338ca', justifyContent: 'flex-start', textTransform: 'none' }}
    >
      {label}
    </Button>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <Button
      onClick={onClick}
      startIcon={icon}
      sx={{ color: '#4338ca', justifyContent: 'flex-start', textTransform: 'none' }}
    >
      {label}
    </Button>
  );
}

function ActivityFeed({ activities }) {
  const groups = groupActivities(activities);
  const labels = Object.keys(groups);

  if (!activities.length) {
    return (
      <Box sx={{ color: '#64748b', py: 5, textAlign: 'center' }}>
        No activity found.
      </Box>
    );
  }

  return labels.map((label) => (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography sx={{ color: '#0f172a', fontSize: 14, fontWeight: 800, mb: 1.5 }}>
        {label}
      </Typography>
      <Stack>
        {groups[label].map((activity) => (
          <ActivityRow key={activity._id} activity={activity} />
        ))}
      </Stack>
    </Box>
  ));
}

function ActivityRow({ activity }) {
  const colors = getActivityColor(activity);
  const detail = getActivityDetail(activity);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '30px 1fr', columnGap: 1.5 }}>
      <Stack alignItems="center">
        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20, mt: 2 }} />
      </Stack>
      <Card
        variant="outlined"
        sx={{
          borderColor: '#dbe3ee',
          borderRadius: 1,
          mb: 1,
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: colors.bg,
                borderRadius: 1,
                color: colors.color,
                display: 'flex',
                flexShrink: 0,
                height: 48,
                justifyContent: 'center',
                width: 48,
              }}
            >
              {getActivityIcon(activity)}
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
                {getActivityTitle(activity)}
              </Typography>
              {detail && (
                <Typography sx={{ color: '#40536b', fontSize: 14, mt: 0.35 }}>
                  {detail}
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: '#0f172a', fontSize: 13 }}>
                {formatTime(activity.createdAt)}
              </Typography>
              <IconButton size="small">
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function SidePanel({ user, activities, notes, trainingPlanSessions }) {
  const activePlan = getActivePlan(trainingPlanSessions);
  const nutritionLogsThisWeek = activities.filter(
    (activity) =>
      getActivityType(activity) === 'nutrition' &&
      activity.createdAt &&
      moment(activity.createdAt).isAfter(moment().subtract(7, 'days'))
  ).length;
  const workoutCount = user.workoutSessionCount ?? 0;
  const latestNote = notes?.[0];

  return (
    <Stack spacing={2}>
      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.25 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 900, mb: 2 }}>
            Activity Insights
          </Typography>
          <Stack spacing={1.5}>
            <InsightRow
              icon={<TrendingUpOutlinedIcon />}
              title="Workout activity"
              detail={`${workoutCount} completed workout${workoutCount === 1 ? '' : 's'}`}
            />
            <InsightRow
              icon={<RestaurantOutlinedIcon />}
              title="Nutrition logging"
              detail={`${nutritionLogsThisWeek} log${nutritionLogsThisWeek === 1 ? '' : 's'} in the last 7 days`}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.25 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <CalendarTodayOutlinedIcon sx={{ color: '#4338ca' }} />
            <Typography sx={{ fontSize: 17, fontWeight: 900 }}>Current Plan</Typography>
          </Stack>
          {activePlan ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontWeight: 900 }}>
                  {activePlan.trainingPlan?.title || 'Untitled plan'}
                </Typography>
                <Chip size="small" label={activePlan.status || 'UNKNOWN'} />
              </Stack>
              <Typography sx={{ color: '#40536b', mt: 1 }}>
                Started {formatDate(activePlan.startedAt || activePlan.enrolledAt)}
              </Typography>
              {activePlan.trainingPlan?._id && (
                <Button
                  component={RouterLink}
                  to={`/trainer/training_plans/${activePlan.trainingPlan._id}`}
                  sx={{ mt: 1.5, px: 0 }}
                >
                  View Plan Details
                </Button>
              )}
            </>
          ) : (
            <Typography sx={{ color: '#64748b' }}>No active plan.</Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.25 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <CalendarTodayOutlinedIcon sx={{ color: '#4338ca' }} />
            <Typography sx={{ fontSize: 17, fontWeight: 900 }}>Upcoming</Typography>
          </Stack>
          <Typography sx={{ color: '#40536b' }}>
            {activePlan?.nextEvent?.title || 'No upcoming sessions'}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <NotesOutlinedIcon sx={{ color: '#4338ca' }} />
              <Typography sx={{ fontSize: 17, fontWeight: 900 }}>Notes</Typography>
            </Stack>
            <Typography sx={{ color: '#4338ca', fontSize: 13, fontWeight: 800 }}>
              {notes?.length || 0}
            </Typography>
          </Stack>
          {latestNote ? (
            <Typography sx={{ color: '#40536b' }}>{latestNote.body}</Typography>
          ) : (
            <Typography sx={{ color: '#64748b', textAlign: 'center' }}>
              No notes yet
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

function InsightRow({ icon, title, detail }) {
  return (
    <Box sx={{ border: '1px solid #eef2f7', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: '#eef2ff',
            borderRadius: 1,
            color: '#4338ca',
            display: 'flex',
            height: 42,
            justifyContent: 'center',
            width: 42,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 900 }}>{title}</Typography>
          <Typography sx={{ color: '#40536b', fontSize: 13, mt: 0.35 }}>{detail}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

const ActivityTab = ({
  token,
  user,
  trainingPlanSessions = [],
  activities = [],
  notes = [],
  onTabChange,
  onTrainingPlanSessionStarted,
}) => {
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [addingTestData, setAddingTestData] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const activePlan = getActivePlan(trainingPlanSessions);

  const filteredActivities = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return activities.filter((activity) => {
      const type = getActivityType(activity);
      const matchesType = typeFilter === 'all' || type === typeFilter;
      const text = [
        getActivityTitle(activity),
        getActivityDetail(activity),
        activity.fromUser?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !normalizedSearch || text.includes(normalizedSearch);

      return matchesType && matchesSearch;
    });
  }, [activities, searchTerm, typeFilter]);

  const handleEnrollDialogClose = (trainingPlanSession) => {
    setShowEnrollDialog(false);
    if (trainingPlanSession?._id) {
      onTrainingPlanSessionStarted?.(trainingPlanSession);
    }
  };

  const handleAddTestData = async () => {
    if (!token || !user?._id || addingTestData) {
      return;
    }

    setAddingTestData(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/admin/users/${user._id}/add_workout_test_data`,
        {
          method: 'POST',
          headers: new Headers({ Authorization: `Bearer ${token}` }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        window.alert(body.message || 'Could not add test data.');
        return;
      }

      window.location.reload();
    } finally {
      setAddingTestData(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Stack spacing={2}>
          <AboutCard token={token} user={user} />
          <QuickActions
            activePlan={activePlan}
            onAddTestData={handleAddTestData}
            onAssignPlan={() => setShowEnrollDialog(true)}
            onTabChange={onTabChange}
          />
        </Stack>
      </Grid>

      <Grid item xs={12} lg={6.5}>
        <Card sx={{ ...cardSx, minHeight: 520 }}>
          <CardContent sx={{ p: 2.25 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography sx={{ fontSize: 24, fontWeight: 900 }}>Activity</Typography>
                <Typography sx={{ color: '#40536b' }}>
                  A timeline of everything your client has done.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Select
                  size="small"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Activity</MenuItem>
                  {filterOptions.slice(1).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  size="small"
                  placeholder="Search activity..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              {filterOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  onClick={() => setTypeFilter(option.value)}
                  color={typeFilter === option.value ? 'secondary' : 'default'}
                  variant={typeFilter === option.value ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>

            <ActivityFeed activities={filteredActivities} />

            {activities.length > filteredActivities.length && (
              <>
                <Divider sx={{ my: 2 }} />
                <Button onClick={() => { setTypeFilter('all'); setSearchTerm(''); }}>
                  Clear filters
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={2.5}>
        <SidePanel
          user={user}
          activities={activities}
          notes={notes}
          trainingPlanSessions={trainingPlanSessions}
        />
      </Grid>

      <EnrollUserIntoTrainingPlanDialog
        user={user}
        open={showEnrollDialog}
        onClose={handleEnrollDialogClose}
      />
    </Grid>
  );
};

export default ActivityTab;
